"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, BookOpen, CircleQuestionMark as HelpCircle, Compass, CircleCheckBig as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
export default function EstimateForm() {
  const [currentPath, setCurrentPath] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPath(window.location.pathname || "/");
    }
  }, []);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      consent: checked
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent before submitting.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", currentPath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsLoading(false);
    }
  };
  const relatedLinks = [{
    title: "Remodeling Planning Guide",
    description: "Step-by-step roadmap to budget, design, and prepare your home renovation.",
    to: "/remodeling-planning-guide",
    icon: BookOpen
  }, {
    title: "FAQ",
    description: "Answers to common questions about timelines, permits, pricing, and our craft.",
    to: "/faq",
    icon: HelpCircle
  }, {
    title: "Process & Planning",
    description: "Explore our proven consultation, architectural design, and build timeline.",
    to: "/process-planning",
    icon: Compass
  }];
  return <section id="relatedlinksandestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Related Links Block */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-foreground">
              Helpful Planning Resources
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-sans">
              Get familiar with our renovation process, pricing expectations, and project timelines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((item, i) => {
            const Icon = item.icon;
            return <div key={item.to} data-index={i}>
                  <Card className="h-full bg-card hover:bg-secondary/40 border-border shadow-sm transition-all duration-200 group flex flex-col justify-between p-6 rounded-xl">
                    <div>
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-4 group-hover:scale-105 transition-transform">
                        <Icon className="size-5" />
                      </div>
                      <h3 className="text-xl font-serif font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground font-sans leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="pt-6 mt-auto">
                      <Button asChild variant="ghost" className="p-0 h-auto font-medium text-foreground group-hover:text-primary hover:bg-transparent">
                        <Link to={item.to} className="inline-flex items-center gap-2 text-sm">
                          <span>Explore guide</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {/* Estimate Form Card */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border/80 bg-card/90 shadow-lg rounded-xl overflow-hidden backdrop-blur-xs">
            <div className="px-6 pt-8 pb-4 sm:px-10 border-b border-border/60 text-center">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-card-foreground tracking-tight">
                Request an Estimate
              </h2>
              <p className="mt-2 text-sm sm:text-base text-muted-foreground font-sans">
                Tell us about your home and goals. We will review your vision and connect promptly.
              </p>
            </div>

            <CardContent className="p-6 sm:p-10">
              {isSuccess ? <div className="text-center py-10 px-4 space-y-4">
                  <div className="size-16 rounded-full bg-secondary mx-auto flex items-center justify-center text-foreground">
                    <CheckCircle2 className="size-10" />
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-card-foreground">
                    Request Received
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base">
                    Thank you for reaching out. Our remodeling specialists will review your project details and get in touch via your preferred contact method.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {errorMessage && <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Notice</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-sans font-medium text-foreground">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="name" name="name" type="text" required value={formData.name} onChange={handleInputChange} placeholder="e.g. Jane Smith" className="bg-background/80" />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-sans font-medium text-foreground">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} placeholder="Your phone number" className="bg-background/80" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-sans font-medium text-foreground">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} placeholder="Your email address" className="bg-background/80" />
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="address" className="font-sans font-medium text-foreground">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="address" name="address" type="text" required value={formData.address} onChange={handleInputChange} placeholder="Decatur, GA or 30030" className="bg-background/80" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Project Type */}
                    <div className="space-y-2">
                      <Label className="font-sans font-medium text-foreground">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => handleSelectChange("project_type", val)} required>
                        <SelectTrigger className="w-full bg-background/80">
                          <SelectValue placeholder="Select a project type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                          <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                          <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                          <SelectItem value="Home Additions">Home Additions</SelectItem>
                          <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                          <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                          <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                          <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preferred Contact Method */}
                    <div className="space-y-2">
                      <Label className="font-sans font-medium text-foreground">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => handleSelectChange("contact_method", val)} required>
                        <SelectTrigger className="w-full bg-background/80">
                          <SelectValue placeholder="Select contact preference" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Text">Text</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Project Timing */}
                    <div className="space-y-2">
                      <Label className="font-sans font-medium text-foreground">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => handleSelectChange("timing", val)} required>
                        <SelectTrigger className="w-full bg-background/80">
                          <SelectValue placeholder="When would you like to begin?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ASAP">ASAP</SelectItem>
                          <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                          <SelectItem value="1-3 months">1-3 months</SelectItem>
                          <SelectItem value="3-6 months">3-6 months</SelectItem>
                          <SelectItem value="Just researching">Just researching</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Estimated Budget */}
                    <div className="space-y-2">
                      <Label className="font-sans font-medium text-foreground">
                        Estimated Budget
                      </Label>
                      <Select value={formData.budget} onValueChange={val => handleSelectChange("budget", val)}>
                        <SelectTrigger className="w-full bg-background/80">
                          <SelectValue placeholder="Select target budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                          <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                          <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                          <SelectItem value="$100,000+">$100,000+</SelectItem>
                          <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-2">
                    <Label htmlFor="details" className="font-sans font-medium text-foreground">
                      Project Details
                    </Label>
                    <Textarea id="details" name="details" value={formData.details} onChange={handleInputChange} placeholder="Share any details, goals, or questions about your home renovation..." rows={4} className="bg-background/80 resize-y" />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="pt-2">
                    <div className="flex items-start gap-3">
                      <Checkbox id="consent" checked={formData.consent} onCheckedChange={handleCheckboxChange} className="mt-1" required />
                      <Label htmlFor="consent" className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed cursor-pointer">
                        I agree to be contacted about my project by phone, text, or email.{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-3">
                    <Button type="submit" disabled={isLoading} className="w-full sm:w-auto min-w-[200px] h-11 text-base font-medium transition-all">
                      {isLoading ? <>
                          <Loader2 className="size-4 animate-spin mr-2" />
                          Submitting...
                        </> : "Submit Estimate Request"}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}